import React, { useState } from "react";
import { ShieldAlert, X } from "lucide-react";
import { useApp } from "../context";

/**
 * Banner shown for child_explorer accounts when parent consent is still pending
 * (72-hour grace window) OR expired. Informs the child that a grown-up needs
 * to confirm the account and flags that some features are restricted.
 *
 * Paired with backend: `consent_status` on user object can be:
 *   - "pending"      : within 72h grace, most features still work
 *   - "expired"      : past 72h, no consent yet, premium + social features gated
 *   - "not_required" : child is 13+, no consent needed
 *   - "approved"     : parent clicked consent link, all good
 */
const ChildConsentBanner = () => {
  const { user } = useApp();
  const [dismissed, setDismissed] = useState(false);

  if (!user || user.role !== "child_explorer") return null;
  const status = user.consent_status;
  if (status !== "pending" && status !== "expired") return null;
  if (dismissed && status === "pending") return null; // allow dismiss only in grace window

  const expired = status === "expired";
  const timeLeft = (() => {
    if (!user.consent_grace_until && !user.consent_grace_end) return null;
    try {
      const end = new Date(user.consent_grace_until || user.consent_grace_end).getTime();
      const hrs = Math.max(0, Math.round((end - Date.now()) / 36e5));
      return hrs;
    } catch { return null; }
  })();

  return (
    <div
      className={`below-header-fixed-tight left-0 right-0 z-40 px-4`}
      data-testid="coppa-consent-banner"
      data-consent-status={status}>
      <div className={`max-w-4xl mx-auto rounded-xl p-3 flex items-center gap-3 shadow-lg backdrop-blur-sm border ${expired ? "bg-amber-500/15 border-amber-400/40" : "bg-blue-500/15 border-blue-400/40"}`}>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${expired ? "bg-amber-500/25" : "bg-blue-500/25"}`}>
          <ShieldAlert size={18} className={expired ? "text-amber-300" : "text-blue-300"} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-fredoka font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)] ${expired ? "text-amber-100" : "text-blue-100"}`}>
            {expired
              ? "A grown-up needs to confirm your account"
              : "Waiting for a grown-up to confirm your account"}
          </p>
          <p className="text-white/85 text-[12px] font-medium leading-relaxed">
            {expired
              ? "Some features like the AI Tutor, Family Games, and the Marketplace are locked until a parent clicks the confirm link we emailed to them."
              : timeLeft != null
                ? `You have ${timeLeft}h left of full access. After that, a few features will be locked until a grown-up clicks the confirm link in their email.`
                : "We emailed your grown-up. Most features work for now, some will lock if they don't confirm soon."}
          </p>
        </div>
        {!expired && (
          <button
            onClick={() => setDismissed(true)}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white shrink-0"
            aria-label="Dismiss"
            data-testid="coppa-banner-dismiss">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChildConsentBanner;
